import { useEffect, useMemo, useRef, useState } from "react";
import Icon from "@/components/Icon";

interface Option { id: number; title: string }

interface Props {
  options: Option[];
  selected: number[];
  onChange: (ids: number[]) => void;
  /** Créer une option à la volée (ex. nouveau chapitre). Si absent, pas de création. */
  onCreate?: (title: string) => Promise<Option>;
  placeholder?: string;
}

/** Champ de sélection multiple à étiquettes : les éléments choisis s'affichent en
 *  chips (croix pour retirer) ; le champ filtre la liste ; Entrée sur un libellé
 *  inconnu le crée (si onCreate). Compact comme un menu déroulant, recherche clavier. */
export default function TagMultiSelect({ options, selected, onChange, onCreate, placeholder }: Props) {
  const [query, setQuery] = useState("");
  const [open, setOpen]   = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);
  const chosen = selected.map((id) => byId.get(id)).filter(Boolean) as Option[];

  const q = query.trim().toLowerCase();
  const matches = options.filter(
    (o) => !selected.includes(o.id) && (!q || o.title.toLowerCase().includes(q)),
  );
  const exact = options.find((o) => o.title.toLowerCase() === q);
  const canCreate = !!onCreate && q.length > 0 && !exact;

  const add = (id: number) => { onChange([...selected, id]); setQuery(""); };
  const remove = (id: number) => onChange(selected.filter((x) => x !== id));
  const create = async () => {
    if (!onCreate || !query.trim()) return;
    const opt = await onCreate(query.trim());
    onChange([...selected, opt.id]);
    setQuery("");
  };

  return (
    <div ref={ref} style={styles.wrap}>
      <div style={styles.field} onClick={() => setOpen(true)}>
        {chosen.map((o) => (
          <span key={o.id} style={styles.chip}>
            {o.title}
            <button style={styles.chipX} onClick={(e) => { e.stopPropagation(); remove(o.id); }} aria-label={`Retirer ${o.title}`} type="button">
              <Icon name="clear" size={11} />
            </button>
          </span>
        ))}
        <input
          style={styles.input}
          value={query}
          placeholder={chosen.length === 0 ? (placeholder ?? "Rechercher…") : ""}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); if (matches.length) add(matches[0].id); else if (canCreate) create(); }
            else if (e.key === "Backspace" && !query && chosen.length) remove(chosen[chosen.length - 1].id);
          }}
        />
      </div>

      {open && (matches.length > 0 || canCreate) && (
        <div style={styles.menu} role="listbox">
          {matches.map((o) => (
            <button key={o.id} style={styles.item} onClick={() => add(o.id)} type="button">{o.title}</button>
          ))}
          {canCreate && (
            <button style={{ ...styles.item, color: "var(--blue)" }} onClick={create} type="button">
              <Icon name="duplicate" size={13} /> Créer « {query.trim()} »
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { position: "relative" },
  field: {
    display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6,
    background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)",
    padding: "5px 8px", minHeight: 38, cursor: "text",
  },
  chip: {
    display: "inline-flex", alignItems: "center", gap: 4, background: "var(--badge-bg)",
    color: "var(--blue)", borderRadius: 999, padding: "2px 6px 2px 10px", fontSize: 12.5, fontWeight: 600,
  },
  chipX: { background: "transparent", border: "none", color: "var(--blue)", cursor: "pointer", display: "inline-flex", padding: 1 },
  input: { flex: 1, minWidth: 90, background: "transparent", border: "none", outline: "none", color: "var(--text)", fontSize: 13, fontFamily: "inherit" },
  menu: {
    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50,
    background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)",
    boxShadow: "var(--shadow-1)", padding: 4, display: "flex", flexDirection: "column", gap: 2, maxHeight: 220, overflowY: "auto",
  },
  item: {
    display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
    background: "transparent", border: "none", borderRadius: "var(--radius-sm)", color: "var(--text)",
    padding: "7px 10px", cursor: "pointer", fontSize: 13,
  },
};
